import graphQLAdapter from "@/adapters/graphQL.adapter.js";
import Order from "@/entities/order.entity.js";
import Layout from "@/entities/layout.entity.js";
import ServiceFactory from "@/services/service.factory";

const fileService = ServiceFactory.create("file");

function getYearAndMonthDay (text) {
  if (!text || text.length === 0) {
    return [];
  }
  // 年が入力されていれば、それ以降の文字列が月日
  let monthDay = [];
  const year = text.reduce((acc, cur) => {
    if (acc.map(o => o.character).includes("年")) {
      monthDay.push(cur);
      return acc;
    }
    return [...acc, cur];
  }, []);
  return [year, monthDay];
}

function getMonthAndDay (text) {
  if (!text || text.length === 0) {
    return [];
  }
  let day = [];
  const month = text.reduce((acc, cur) => {
    if (acc.map(o => o.character).includes("月")) {
      day.push(cur);
      return acc;
    }
    return [...acc, cur];
  }, []);
  return [month, day];
}

const normalizeNameArray = (value) => (Array.isArray(value) ? value : []);

async function normalizeOrderDetail (order) {
  const detail = order.detail || {};
  const columns = Array.isArray(detail.columns) ? detail.columns : [];
  detail.columns = columns;
  order.detail = detail;

  columns.forEach(column => {
    column.buddhistName = normalizeNameArray(column.buddhistName);
    column.secularName = normalizeNameArray(column.secularName);
    column.underCharacters = normalizeNameArray(column.underCharacters);
    column.dateOfDeath = normalizeNameArray(column.dateOfDeath);
    column.ageAtDeathPrefix = normalizeNameArray(column.ageAtDeathPrefix);
    column.ageAtDeath = normalizeNameArray(column.ageAtDeath);
    column.ageUnitCharacter = normalizeNameArray(column.ageUnitCharacter);
  });

  // 異体字画像をフルパスにする
  await Promise.all(
    columns.map(column => {
      return Promise.all(
        normalizeNameArray(column.buddhistName).map(async char => {
          if (!char.imageUrl) {
            return;
          }
          if (char.imageUrl.match(/^http/)) {
            return;
          }
          char.imageUrl = await fileService.get(char.imageUrl);
        })
      );
    })
  );

  // 古いデータの場合ageAtDeathPrefixとageUnitCharacterがdetailに依存しているので現在の構造に合わせてcolumnsに展開する
  columns.map(column => {
    const ageAtDeathPrefix = normalizeNameArray(detail.ageAtDeathPrefix);
    const ageUnitCharacter = normalizeNameArray(detail.ageUnitCharacter);
    if (!column.ageAtDeathPrefix) {
      column.ageAtDeathPrefix = ageAtDeathPrefix;
    }
    if (!column.ageUnitCharacter) {
      column.ageUnitCharacter = ageUnitCharacter;
    }
  });

  // dateOfDeathを分解する
  columns.map(column => {
    const [year, monthDay] = getYearAndMonthDay(column.dateOfDeath);
    const [month, day] = getMonthAndDay(monthDay);
    column.dateOfDeathYear = year || [];
    column.dateOfDeathMonth = month || [];
    column.dateOfDeathDay = day || [];
  });
}

/**
 * Order Service Class
 *
 * @export
 * @class
 */
export default class {
  constructor () {
    // eslint-disable-next-line
    this.adapter = new graphQLAdapter();

    // Orders
    this.getOrders = getOrders.bind(this);
    this.exportOrders = exportOrders.bind(this);
    this.getOrder = getOrder.bind(this);
    this.orderUpdateStatus = orderUpdateStatus.bind(this);
    this.orderDelete = orderDelete.bind(this);
    this.orderUpdate = orderUpdate.bind(this);
    this.orderConfirm = orderConfirmDate.bind(this);
    this.getOrderCount = getOrderCount.bind(this);
    // Order Details
    this.getKikanOrderDetail = getKikanOrderDetail.bind(this);
    // Layouts
    this.getLayouts = getLayouts.bind(this);
    this.orderCreate = orderCreate.bind(this);
    // Assign staff
    this.orderAssignStaff = orderAssignStaff.bind(this);
    // OrderCustomFieldConfig
    this.getOrderCustomFieldConfig = getOrderCustomFieldConfig.bind(this);
    this.updateOrderCustomFieldConfig = updateOrderCustomFieldConfig.bind(this);
    // OrderCustomFieldConfigSource
    this.getOrderCustomFieldConfigSource = getOrderCustomFieldConfigSource.bind(this);
    this.updateOrderCustomFieldConfigSource = updateOrderCustomFieldConfigSource.bind(this);
    // get defaultExpectedShippingDate
    this.defaultExpectedShippingDate = defaultExpectedShippingDate.bind(this);

    // mutation orderStartLayoutRevision
    this.orderStartLayoutRevision = orderStartLayoutRevision.bind(this);
    // Layout Image mutations
    this.orderAddLayoutImage = orderAddLayoutImage.bind(this);
    this.orderRemoveLayoutImage = orderRemoveLayoutImage.bind(this);
    this.orderApproveLayoutImage = orderApproveLayoutImage.bind(this);
    this.orderUnapproveLayoutImage = orderUnapproveLayoutImage.bind(this);
  }
  /**
   * Get the list of orders
   *
   * @static
   * @returns {Promise<Object|null>} Promise object represents the list of orders
   */
  async list (options) {
    await this.adapter.init();
    const res = await this.getOrders(options);
    return res.data.orders;
  }
  async export (options) {
    await this.adapter.init();
    const res = await this.exportOrders(options);
    return res.data.orderExport;
  }
  async orderCount (option) {
    await this.adapter.init();
    const res = await this.getOrderCount(option);
    return res.data.orderCount;
  }
  async get (option) {
    await this.adapter.init();
    let res = await this.getOrder(option);
    if (!res || !res.data || !res.data.order) {
      return null;
    }
    const order = res.data.order;

    await normalizeOrderDetail(order);

    // 子注文の detail も同様に正規化する
    if (Array.isArray(order.childOrders)) {
      await Promise.all(
        order.childOrders.map(child => normalizeOrderDetail(child))
      );
    }

    return Order.fromJson(order);
  }
  async updateStatus (option) {
    try {
      const res = await this.orderUpdateStatus(option);
      if (res.data.order) {
        return Order.fromJson(res.data.order);
      }
      return res;
    } catch (err) {
      console.warn(err);
      return err;
    }
  }
  /**
   * 「レイアウト調整を開始する」ボタンよりステータスを遷移させるときに mutation orderStartLayoutRevision を利用するように変更する
   * https://github.com/TheDesignium/nanokos/issues/194
  */
  async orderStartLayoutRevision (option) {
    try {
      const res = await this.orderStartLayoutRevision(option);
      if (res.data.orderStartLayoutRevision) {
        return Order.fromJson(res.data.orderStartLayoutRevision);
      }
      return res;
    } catch (err) {
      console.warn(err);
      return err;
    }
  }
  async updateOrder (option) {
    try {
      const res = await this.orderUpdate(option);
      if (res.data.orderUpdate) {
        return Order.fromJson(res.data.orderUpdate);
      }
      return res;
    } catch (err) {
      console.warn(err);
      return err;
    }
  }
  async orderConfirm (option) {
    try {
      const res = await this.orderConfirmDate(option);
      if (res.data.orderConfirm) {
        return Order.fromJson(res.data.orderConfirm);
      }
      return res;
    } catch (err) {
      console.warn(err);
      return err;
    }
  }
  async delete (option) {
    try {
      const res = await this.orderDelete(option);
      if (res.data.order) {
        return Order.fromJson(res.data.order);
      }
      return res;
    } catch (err) {
      console.warn(err);
      return err;
    }
  }
  async layouts (option) {
    await this.adapter.init();
    const res = await this.getLayouts(option);

    return res.data.layouts.map(el => Layout.fromJson(el));
  }
  async create (option) {
    const res = await this.orderCreate(option);
    if (res.data.orderCreate) {
      return Order.fromJson(res.data.orderCreate);
    }
    return res;
  }

  async updateAssignStaff (option) {
    try {
      const res = await this.orderAssignStaff(option);
      if (res.data.order) {
        return Order.fromJson(res.data.order);
      }
      return res;
    } catch (err) {
      console.warn(err);
      return err;
    }
  }

  /**
   * Get the defaultExpectedShippingDate
   *
   * @static
   * @returns {Promise<Object|null>} Promise object represents the defaultExpectedShippingDate
   */
  async getDefaultExpectedShippingDate () {
    await this.adapter.init();

    const res = await this.defaultExpectedShippingDate();
    if (
      res.data &&
      res.data.defaultExpectedShippingDate
    ) {
      return res.data.defaultExpectedShippingDate;
    }

    return null;
  }
}
function getOrders (
  options = {
    q: {
      status: null,
      clientGroupId: null,
      deliveryDestinationId: null,
      customerName: null,
      zipcode: null,
      address: null,
      tel: null,
      fortyNinthMemorialServiceDateStart: null,
      fortyNinthMemorialServiceDateEnd: null,
      contractDateStart: null,
      contractDateEnd: null,
      deliveryDateStart: null,
      deliveryDateEnd: null,
      customerNumber: null,
      note: null,
      productName: null,
      productVariationId: null,
      orderNumber: null,
      orderedAtStart: null,
      orderedAtEnd: null,
    },
  }
) {
  // console.log(options);
  const query = `
  query (
    $q: OrderFilterConditions
    $page: Int
    $pageSize: Int
    $sortKey: String
    $sortDirection: String
    $mineFlg: Boolean
  ) {
    orders(
      q: $q
      page: $page
      pageSize: $pageSize
      sortKey: $sortKey
      sortDirection: $sortDirection
      mineFlg: $mineFlg
    ) {
      total
      totalPage
      currentPage
      pageSize
      orders {
        id
        hasUnReadMessage
        messageCount
        unreadMessageCount
        status {
          code
          name
        }
        clientGroup {
          id
          name
          showSKWriteFont
          showGoldFilmColor
          showHandCarveWriteFont
          region
        }
        deliveryDestination {
          id
          name
        }
        client {
          id
          name
          clientGroups {
            id
            name
          }
        }
        customer {
          name
          zipcode
          address
          tel
          familyTemple
          sect
          note
        }
        customFields {
          customFieldNumber
          type
          name
          value
          order
        }
        detail {
          product {
            id
            name
            variations {
              size
            }
          }
          productVariation {
            size
          }
          typography {
            printType
          }
          deliveryDate
          quantity
        }
        statement {
          items {
            id
            productCD
            productName
            unitPrice
            quantity
            price
            note
            orderId
          }
          note
          tax
          subtotal
        }
        parentOrder {
          id
        }
        childOrders {
          id
          siblingOrder
          hasUnReadMessage
          status {
            code
            name
          }
          customer {
            name
          }
          detail {
            product {
              name
            }
            productVariation {
              size
            }
          }
          orderedAt
        }
        orderedAt
        orderNumber
        createdAt
        orderedAt
        modifiedAt
      }
    }
  }`;
  return this.adapter.query(query, options);
}
function exportOrders (
  options = {
    q: {
      status: null,
      clientGroupId: null,
      deliveryDestinationId: null,
      customerName: null,
      zipcode: null,
      address: null,
      tel: null,
      fortyNinthMemorialServiceDateStart: null,
      fortyNinthMemorialServiceDateEnd: null,
      contractDateStart: null,
      contractDateEnd: null,
      deliveryDateStart: null,
      deliveryDateEnd: null,
      customerNumber: null,
      note: null,
      productName: null,
      productVariationId: null,
      orderNumber: null,
      orderedAtStart: null,
      orderedAtEnd: null,
    },
  }
) {
  const query = `
  query ($q: OrderFilterConditions) {
    orderExport(q: $q) {
      url
    }
  }`;
  return this.adapter.query(query, options);
}

function getOrderCount (
  options = {
    status: null,
    q: {
      status: null,
      clientGroupId: null,
      customerName: null,
      zipcode: null,
      address: null,
      tel: null,
      fortyNinthMemorialServiceDateStart: null,
      fortyNinthMemorialServiceDateEnd: null,
      contractDateStart: null,
      contractDateEnd: null,
      deliveryDateStart: null,
      deliveryDateEnd: null,
      customerNumber: null,
      note: null,
      orderedAtStart: null,
      orderedAtEnd: null,
      productName: null,
      productVariationId: null,
      orderNumber: null,
    },
  }) {
  const query = `query orderCount(
    $status: OrderStatusCode!
    $q: OrderFilterConditions
  ) {
    orderCount (
      status: $status,
      q: $q
    )
  }`;
  return this.adapter.query(query, options);
}
function orderUpdate (options = {}) {
  const mutation = `mutation ($orderId: ID!,$order: OrderUpdateInput!){
    orderUpdate(orderId: $orderId, order: $order) {
      detail {
        fixedShippingDate
        attachments
      }
      customer {
        name
        zipcode
        address
        tel
        familyTemple
        sect
        note
        customerNumber
      }
    }
  }
 `;

  return this.adapter.mutation(mutation, options);
}

function orderConfirmDate (options = {}) {
  const mutation = `mutation ($orderId: ID!, $shippingDate: String!) {
    orderConfirm(orderId: $orderId, shippingDate: $shippingDate) {
      id
      status {
        code
        name
      }
      detail {
        fixedShippingDate
        attachments
      }
      customer {
        name
      }
      createdAt
      modifiedAt
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function getOrder (options = {}) {
  const query = `query($orderId: ID!){
    order(orderId: $orderId) {
      id
      hasUnReadMessage
      messageCount
      unreadMessageCount
      customer {
        name
        zipcode
        address
        tel
        sect
        note
        familyTemple
        customerNumber
      }
      customFields {
        customFieldNumber
        type
        name
        value
        order
      }
      status {
        code
        name
      }
      clientGroup {
        id
        name
        showSKWriteFont
        showGoldFilmColor
        showHandCarveWriteFont
        region
      }
      deliveryDestination {
        id
        name
      }
      client {
        id
        name
        clientGroups {
          id
          name
        }
      }
      staff{
        id
        name
      }
      detail {
        product {
          id
          name
          number
          imageUrl
          category {
            id
            name
          }
          variations {
            id
            size
            stock
            stockExpression
          }
        }
        productVariation {
          id
          size
        }
        azukariProductName
        azukariProductSize
        quantity
        orderType {
          type
          nameCount
        }
        layout {
          id
          name
        }
        typography {
          printType
          font
          textColorFront
          textColorBack
        }
        familyEmblem {
          id
          name
          imageUrl
          imageUrlForTablet
          inputCode
        }
        columns {
          order
          buddhistName {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          isSecularNameSuffixNeeded
          secularNameSuffix {
            id
            imageUrl
          }
          secularName {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          underCharacters {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          title
          dateOfDeath {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          isOldCharacterForDateOfDeathNeeded
          oldCharacterForDateOfDeath
          ageAtDeathPrefix {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          ageAtDeath {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          ageUnitCharacter {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
        }
        fortyNinthMemorialServiceDate
        contractDate
        deliveryDate
        isDiedCharacterNeeded
        diedCharacter {
          variantCharacterId
          character
          imageUrl
          points
          comment
        }
        ageAtDeathPrefix {
          variantCharacterId
          character
          imageUrl
          points
          comment
        }
        ageUnitCharacter {
          variantCharacterId
          character
          imageUrl
          points
          comment
        }
        secularNamePrefix {
          variantCharacterId
          character
          imageUrl
          points
          comment
        }
        underCharacters {
          variantCharacterId
          character
          imageUrl
          points
          comment
        }
        underCharactersPlacement
        secularNamePrefixPlacement
        ageAtDeathPrefixPlacement
        sanskritCharacterOrFamilyEmblem
        sanskritCharacter {
          id
          sect
          imageUrl
          inputCode
          imageUrlForTablet
        }
        sanskritCharacterBack {
          id
          sect
          imageUrl
          inputCode
          imageUrlForTablet
        }
        customSanskritCharacterUrl
        customSanskritCharacterBackUrl
        customFamilyEmblemUrl
        isNeededLayoutReply
        fixedShippingDate
        expectedShippingDate
        signature
        attachments
        hostName {
          variantCharacterId
          character
          imageUrl
          points
          comment
        }
      }
      statement {
        items {
          id
          productCD
          productName
          unitPrice
          quantity
          price
          note
          orderId
        }
        note
        tax
        subtotal
      }
      layoutImages {
        id
        orderId
        imageKey
        fileName
        isApproved
        createdAt
      }
      parentOrder {
        id
      }
      childOrders {
        id
        siblingOrder
        status {
          code
          name
        }
        customer {
          name
          zipcode
          address
          tel
          sect
          note
          familyTemple
          customerNumber
        }
        customFields {
          customFieldNumber
          type
          name
          value
          order
        }
        detail {
          product {
            id
            name
            number
            imageUrl
            category {
              id
              name
            }
            variations {
              id
              size
              stock
              stockExpression
            }
          }
          productVariation {
            id
            size
          }
          azukariProductName
          azukariProductSize
          quantity
          orderType {
            type
            nameCount
          }
          layout {
            id
            name
          }
          typography {
            printType
            font
            textColorFront
            textColorBack
          }
          columns {
            order
            buddhistName {
              variantCharacterId
              character
              imageUrl
              points
              comment
            }
            isSecularNameSuffixNeeded
            secularNameSuffix {
              id
              imageUrl
            }
            secularName {
              variantCharacterId
              character
              imageUrl
              points
              comment
            }
            underCharacters {
              variantCharacterId
              character
              imageUrl
              points
              comment
            }
            title
            dateOfDeath {
              variantCharacterId
              character
              imageUrl
              points
              comment
            }
            isOldCharacterForDateOfDeathNeeded
            oldCharacterForDateOfDeath
            ageAtDeathPrefix {
              variantCharacterId
              character
              imageUrl
              points
              comment
            }
            ageAtDeath {
              variantCharacterId
              character
              imageUrl
              points
              comment
            }
            ageUnitCharacter {
              variantCharacterId
              character
              imageUrl
              points
              comment
            }
          }
          fortyNinthMemorialServiceDate
          contractDate
          deliveryDate
          isDiedCharacterNeeded
          diedCharacter {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          ageAtDeathPrefix {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          ageUnitCharacter {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          secularNamePrefix {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          underCharacters {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
          underCharactersPlacement
          secularNamePrefixPlacement
          ageAtDeathPrefixPlacement
          sanskritCharacterOrFamilyEmblem
          sanskritCharacter {
            id
            sect
            imageUrl
            inputCode
            imageUrlForTablet
          }
          sanskritCharacterBack {
            id
            sect
            imageUrl
            inputCode
            imageUrlForTablet
          }
          customSanskritCharacterUrl
          customSanskritCharacterBackUrl
          customFamilyEmblemUrl
          isNeededLayoutReply
          fixedShippingDate
          expectedShippingDate
          signature
          attachments
          hostName {
            variantCharacterId
            character
            imageUrl
            points
            comment
          }
        }
        layoutImages {
          id
          orderId
          imageKey
          fileName
          isApproved
          createdAt
        }
        createdAt
        modifiedAt
      }
      orderedAt
      orderNumber
      createdAt
      modifiedAt
    }
  }`;
  return this.adapter.query(query, options);
}
function getKikanOrderDetail (options = {}) {
  const query = `
  query kikanOrderDetail($orderId:ID!) {
    kikanOrderDetail (orderId: $orderId){
      shippingDate
      itemFee {
        name
        productVariationId
        denpyogyo
        unitPrice
        quantity
        price
        tekiyo
        kyakuchuno
        taxRate
        taxAmount
      }
      buddhistNameFee {
        name
        productVariationId
        denpyogyo
        unitPrice
        quantity
        price
        tekiyo
        kyakuchuno
        taxRate
        taxAmount
      }
      layoutFee {
        name
        productVariationId
        denpyogyo
        unitPrice
        quantity
        price
        tekiyo
        kyakuchuno
        taxRate
        taxAmount
      }
      packingFee {
        name
        productVariationId
        denpyogyo
        unitPrice
        quantity
        price
        tekiyo
        kyakuchuno
        taxRate
        taxAmount
      }
      deliveryFee {
        name
        productVariationId
        denpyogyo
        unitPrice
        quantity
        price
        tekiyo
        kyakuchuno
        taxRate
        taxAmount
      }
      remarks
      subtotal
      taxTotal
      total
    }
  }
  `;
  return this.adapter.query(query, options);
}
function orderUpdateStatus (options = {}) {
  const mutation = `mutation ($orderId: ID!, $status: OrderStatusCode!){
    orderUpdateStatus(orderId: $orderId, status: $status) {
      id
      status {
        code
        name
      }
      detail {
        fixedShippingDate
        attachments
        fortyNinthMemorialServiceDate
        contractDate
        deliveryDate
      }
      customer {
        name
      }
      createdAt
      modifiedAt
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function getLayouts (
  options = {
    type: null,
    nameCount: null,
  }
) {
  const query = `
  query(
    $type: String,
    $nameCount: Int,
  ){
    layouts(q: {
      type: $type,
      nameCount: $nameCount,
    }) {
      id
      name
      imageUrl
    }
  }`;
  return this.adapter.query(query, options);
}

function orderCreate (
  options = {
    order: {
      status: null,
      parentOrderId: null,
      detail: null,
      customer: {
        name: null,
        zipcode: null,
        address: null,
        tel: null,
      },
    },
  }
) {
  const mutation = `mutation ($order:OrderCreateInput! ){
    orderCreate(order:$order) {
      id,
      detail {
        expectedShippingDate
        fixedShippingDate
        attachments
        fortyNinthMemorialServiceDate
        contractDate
        deliveryDate
      }
      status{
        code
      }
      parentOrder {
        id
      }
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function orderDelete (options = {}) {
  const mutation = `mutation ($orderId: ID!){
    orderDelete(orderId: $orderId) {
      id
      createdAt
      modifiedAt
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function orderAssignStaff (options) {
  const mutation = `mutation ($orderId: ID!, $staffId: ID!){
    orderAssignStaff(orderId: $orderId, staffId: $staffId) {
      id,
      staff {
        id,
        name
      }
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

// OrderCustomFieldConfig > Query
function getOrderCustomFieldConfig (options) {
  const query = `
  query orderCustomFieldConfig (
    $clientGroupId: ID!
  ) {
    orderCustomFieldConfig (
      clientGroupId: $clientGroupId
    ) {
      itemSettings {
        customFieldNumber
        type
        name
        listItems
        defaultValue
        enabled
        required
        order
      }
    }
  }
  `;
  return this.adapter.query(query, options);
}

// OrderCustomFieldConfig > Mutation
function updateOrderCustomFieldConfig (options) {
  const mutation = `
  mutation (
    $clientGroupId: ID!,
    $input: OrderCustomFieldConfigInput!
  ) {
    orderCustomFieldConfigUpdate(
      clientGroupId: $clientGroupId,
      input: $input
    ) {
      itemSettings {
        customFieldNumber
        type
        name
        listItems
        defaultValue
        enabled
        required
        order
      }
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

// OrderCustomFieldConfigSource > Query
function getOrderCustomFieldConfigSource (options) {
  const query = `
  query orderCustomFieldConfigSource($clientGroupId: ID!) {
    orderCustomFieldConfigSource(clientGroupId: $clientGroupId)
  }
  `;
  return this.adapter.query(query, options);
}

// OrderCustomFieldConfigSource > Mutation
function updateOrderCustomFieldConfigSource (options) {
  const mutation = `
  mutation (
    $clientGroupId: ID!,
    $sourceClientGroupId: ID
  ) {
    orderCustomFieldConfigSourceUpdate(
      clientGroupId: $clientGroupId,
      sourceClientGroupId: $sourceClientGroupId
    )
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function defaultExpectedShippingDate (options = {}) {
  const query = `query defaultExpectedShippingDate {
    defaultExpectedShippingDate
  }`;
  return this.adapter.query(query, options);
}

function orderStartLayoutRevision (options = {}) {
  const mutation = `mutation ($orderId: ID!, $image: String){
    orderStartLayoutRevision(orderId: $orderId, image: $image) {
      id
      status {
        code
      }
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function orderAddLayoutImage (options = {}) {
  const mutation = `mutation ($orderId: ID!, $image: String!, $fileName: String){
    orderAddLayoutImage(orderId: $orderId, image: $image, fileName: $fileName) {
      id
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function orderRemoveLayoutImage (options = {}) {
  const mutation = `mutation ($orderId: ID!, $layoutImageId: ID!){
    orderRemoveLayoutImage(orderId: $orderId, layoutImageId: $layoutImageId) {
      id
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function orderApproveLayoutImage (options = {}) {
  const mutation = `mutation ($orderId: ID!, $layoutImageId: ID!){
    orderApproveLayoutImage(orderId: $orderId, layoutImageId: $layoutImageId) {
      id
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}

function orderUnapproveLayoutImage (options = {}) {
  const mutation = `mutation ($orderId: ID!, $layoutImageId: ID!){
    orderUnapproveLayoutImage(orderId: $orderId, layoutImageId: $layoutImageId) {
      id
    }
  }
  `;
  return this.adapter.mutation(mutation, options);
}
